import { usePageLoadSailthruTracking } from '@/hooks/useSailthruTracking';
import type { IContentTags } from '@/interfaces';
import { ContentType } from '@/interfaces';
import { adjustImageAsset } from '@/utils/imageUtils';
import { truncate } from '@/utils/strings';
import Head from 'next/head';
import { useRouter } from 'next/router';

export interface IHeadMetaProps {
    /**
     * The title of this content
     */
    title: string;
    /**
     * Override the canonical path to this content, in case it doesn't match the current URL
     * https://en.wikipedia.org/wiki/Canonical_link_element
     */
    canonicalPathOverride?: string;
    /**
     * Type of content this page is
     * @default website
     */
    type?: ContentType;
    /**
     * The short description of this content
     */
    description?: string;
    /**
     * A URL to an image. Template URLs(-1x-1) will be replaced with an appropriate size
     */
    image?: string;
    /**
     * ISO8601 datetime of when this content was released
     * */
    publishDate?: string;
    /**
     * Video length in seconds
     * */
    duration?: number;
    /**
     * A collection of tags for this content
     */
    tags?: IContentTags;
}

/**
 * Adds a collection of meta tags to the page <Head>. This should be used in the NextJS src/pages/page.tsx files.
 * Translations are not supported so <HeadTitle /> will have to be used in sub-components if they need to be translated.
 * HeadTitle will be later in the DOM tree so will override the default <title> set in HeadMeta.
 */
export const HeadMeta = ({
    title,
    canonicalPathOverride,
    type,
    description,
    image,
    duration,
    publishDate,
    tags,
}: IHeadMetaProps) => {
    const { asPath } = useRouter();
    usePageLoadSailthruTracking(canonicalPathOverride || asPath, tags);
    const url = `https://www.bnef.com${(canonicalPathOverride || asPath).trim()}`;
    const ogType = contentToType(type);

    return (
        <Head>
            <title>{buildSiteTitle(title)}</title>
            <meta property="og:title" content={title} />
            <meta property="og:type" content={ogType} />
            <link rel="canonical" href={url} />
            <meta property="og:url" content={url} />
            {publishDate && <meta name="sailthru.date" content={publishDate} />}
            {tags && <meta name="sailthru.tags" content={flattenTags(tags)} />}
            {description && (
                <>
                    <meta name="description" content={truncate(description, 300)} />
                    <meta property="og:description" content={truncate(description, 300)} />
                </>
            )}
            {image && (
                <>
                    <meta name="twitter:card" content="summary_large_image" />
                    {/* Some sites don't support webp in og:image yet */}
                    <meta property="og:image" content={adjustImageAsset(image, 640, -1, false)} />
                </>
            )}
            {ogType === OgType.video && (
                <>
                    {duration && <meta property="og:video:duration" content={duration.toString(10)} />}
                    {publishDate && <meta property="og:video:release_date" content={publishDate} />}
                </>
            )}
            {ogType === OgType.article && (
                <>
                    <meta property="og:article:section" content={type} />
                    {publishDate && <meta property="og:article:published_time" content={publishDate} />}
                </>
            )}
        </Head>
    );
};

export const HeadTitle = ({ title }: { title: string }) => (
    <Head>
        <title>{buildSiteTitle(title)}</title>
    </Head>
);

const buildSiteTitle = (baseTitle?: string) => {
    const siteTitle = 'BloombergNEF';
    if (baseTitle && baseTitle !== siteTitle) {
        return `${baseTitle} | ${siteTitle}`;
    }
    return siteTitle;
};

const flattenTags = (tags: IContentTags): string => {
    return tags.join(',');
};

const contentToType = (contentType?: ContentType): OgType => {
    switch (contentType) {
        case undefined:
        case null:
            return OgType.website;
        case ContentType.video:
            return OgType.video;
        default:
            return OgType.article;
    }
};

enum OgType {
    website = 'website',
    video = 'video.other',
    article = 'article',
}
